import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@saas-kit/ui';

import { getQueueOverview } from './apiWorkstation';
import { ForceCallDialog } from './ForceCallDialog';
import { formatWait } from './qmsTime';
import type { QmsCounter } from './types';

interface Props {
  counters: QmsCounter[];
  canForceCall: boolean;
}

interface DialogTarget {
  tokenId: string;
  serviceId: string;
  displayCode: string;
}

/** Live, tenant-wide waiting queue grouped by service. Authorized staff can
 *  force-call any waiting token straight onto a counter. */
export function LiveQueuePanel({ counters, canForceCall }: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const [target, setTarget] = useState<DialogTarget | null>(null);

  const queueQuery = useQuery({
    queryKey: ['qms-queue', tenantId],
    queryFn: getQueueOverview,
    enabled: Boolean(tenantId),
    refetchInterval: 5_000,
  });

  const overview = queueQuery.data;
  const groups = (overview?.services ?? []).filter(
    (group) => group.waiting_count > 0,
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('issue.ops.queue_title')}</CardTitle>
        {overview ? (
          <Badge variant="secondary">
            {t('issue.ops.queue_total', { count: overview.total_waiting })}
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {queueQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('issue.ops.queue_empty')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.service_id} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground">
                  {group.service_name}
                </span>
                <Badge variant="secondary">
                  {t('issue.ops.queue_total', { count: group.waiting_count })}
                </Badge>
              </div>
              <ul className="space-y-1">
                {group.tokens.map((tok) => (
                  <li
                    key={tok.id}
                    className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
                  >
                    <span className="font-mono text-sm font-medium text-foreground">
                      {tok.display_code}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {formatWait(tok.wait_seconds)}
                    </span>
                    {canForceCall ? (
                      <Button
                        variant="secondary"
                        className="h-7 px-2 text-xs"
                        onClick={() =>
                          setTarget({
                            tokenId: tok.id,
                            serviceId: group.service_id,
                            displayCode: tok.display_code,
                          })
                        }
                      >
                        {t('issue.ops.force_call')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </CardContent>
      {canForceCall && target ? (
        <ForceCallDialog
          open
          tokenId={target.tokenId}
          serviceId={target.serviceId}
          displayCode={target.displayCode}
          counters={counters}
          onClose={() => setTarget(null)}
        />
      ) : null}
    </Card>
  );
}
